/**
 * What tells two journeys on the same route apart (R8 · D-F): the citizen's
 * name for the journey — or, without one, the company behind it or its
 * scenario — and the day it started.
 *
 * Drawn the same on every surface that lists journeys side by side: the
 * switcher, "My journeys", the archived rows, the builder's route advice and
 * the invitation's journey choice. Formats what it is given; decides nothing.
 */
import { useLocale, useTranslation } from '@tonnie/locale';
import { cn } from '@tonnie/ui';

import { journeyScenarioLabel } from '../utils/journey-scenario-label';

export interface JourneyNameLineProps {
  label: string | null | undefined;
  /** The companies on the journey, earliest first — the first names it when it has no label. */
  companyNames?: readonly string[];
  scenario?: string | null;
  /** ISO — activation, else creation. */
  startedAt: string;
  className?: string;
}

export function JourneyNameLine({
  label,
  companyNames = [],
  scenario,
  startedAt,
  className,
}: JourneyNameLineProps) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const name = label || companyNames[0] || journeyScenarioLabel(scenario, t);

  return (
    <p className={cn('text-xs text-muted-foreground', className)} data-testid="journey-name-line">
      {name && (
        <>
          <span
            className={cn('font-medium', label ? 'text-foreground' : undefined)}
            data-testid="journey-name"
          >
            {name}
          </span>
          <span className="mx-1.5" aria-hidden="true">
            ·
          </span>
        </>
      )}
      {t('journey.manage.startedOn', { date: formatDate(startedAt) })}
    </p>
  );
}
